import { ref, computed, shallowRef } from "vue";
import { defineStore } from "pinia";
import type { WordleMonthlyScoreDocument } from "@kekkle/shared/types/documents/wordle-monthly-score-document/wordle-monthly-score-document";
import type { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { fetchMonthlyScores } from "@/helpers/firestore/Wordle6MonthlyScoreService";
import {
  getBooleanFromLocalStorage,
  setLocalStorageItem,
} from "@/helpers/utils/local-storage";
import { useGroupsStore } from "@/stores/modules/groups/groups";

/**
 * Wordle6 monthly scores store for managing monthly leaderboards
 * Uses Pinia with Composition API and TypeScript
 */
export const useWordle6MonthlyScoresStore = defineStore(
  "wordle6MonthlyScores",
  () => {
    const groupsStore = useGroupsStore();

    /* State */
    const selectedMonth = ref<number>(new Date().getMonth() + 1);
    const selectedYear = ref<number>(new Date().getFullYear());
    const monthlyScores = ref<Record<string, WordleMonthlyScoreDocument[]>>({});
    const monthlyScoresOnlyKnownUsers = ref<
      Record<string, WordleMonthlyScoreDocument[]>
    >({});
    const fetchingMonthlyScores = ref<Record<string, boolean>>({
      "1997-1": false,
    });
    const fetchingMonthlyScoresOnlyKnownUsers = ref<Record<string, boolean>>({
      "1997-1": false,
    });
    const onlyKnownUsers = ref<boolean>(
      getBooleanFromLocalStorage("onlyKnownUsersWordle6MonthlyScores", true),
    );
    // Use shallowRef to prevent deep reactivity on Firestore snapshots
    const lastDocuments = shallowRef<
      Record<string, QueryDocumentSnapshot<DocumentData> | undefined>
    >({});
    const hasMoreData = ref<Record<string, boolean>>({});

    /* Computed / Getters */
    const currentPath = computed(
      () => `${selectedYear.value}-${selectedMonth.value}`,
    );

    const currentMonthlyScores = computed(() => {
      const path = currentPath.value;
      if (onlyKnownUsers.value) {
        return monthlyScoresOnlyKnownUsers.value[path] ?? [];
      } else {
        return monthlyScores.value[path] ?? [];
      }
    });

    const isFetchingMonthlyScores = computed(() => {
      const path = currentPath.value;
      if (onlyKnownUsers.value) {
        return fetchingMonthlyScoresOnlyKnownUsers.value[path] ?? false;
      } else {
        return fetchingMonthlyScores.value[path] ?? false;
      }
    });

    const hasMore = computed(() => {
      const path = currentPath.value;
      return hasMoreData.value[path] ?? false;
    });

    /* Actions */
    function setSelectedMonth(month: number): void {
      selectedMonth.value = month;
    }

    function setSelectedYear(year: number): void {
      selectedYear.value = year;
    }

    function setOnlyKnownUsersWordleMonthlyScores(value: boolean): void {
      onlyKnownUsers.value = value;
      setLocalStorageItem(
        "onlyKnownUsersWordle6MonthlyScores",
        JSON.stringify(value),
      );
    }

    function reset(): void {
      monthlyScores.value = {};
      monthlyScoresOnlyKnownUsers.value = {};
      onlyKnownUsers.value = true;
      lastDocuments.value = {};
      hasMoreData.value = {};
      setLocalStorageItem("onlyKnownUsersWordle6MonthlyScores", "true");
    }

    async function fetchMonthlyScoresAction(loadMore = false): Promise<void> {
      const year = selectedYear.value;
      const month = selectedMonth.value;
      const path = `${year}-${month}`;
      const knownUsers = onlyKnownUsers.value;

      let userFilter: string[] = [];
      if (knownUsers) {
        userFilter = groupsStore.allGroupMembersUuids;
      }

      // Set fetching state
      if (knownUsers) {
        fetchingMonthlyScoresOnlyKnownUsers.value[path] = true;
      } else {
        fetchingMonthlyScores.value[path] = true;
      }

      const lastDoc = loadMore ? lastDocuments.value[path] : undefined;
      const result = await fetchMonthlyScores(year, month, userFilter, {
        pageSize: 5,
        lastDoc,
      });

      // Store results
      if (knownUsers) {
        if (loadMore) {
          monthlyScoresOnlyKnownUsers.value[path] = [
            ...(monthlyScoresOnlyKnownUsers.value[path] ?? []),
            ...result.docs,
          ];
        } else {
          monthlyScoresOnlyKnownUsers.value[path] = result.docs;
        }
      } else {
        if (loadMore) {
          monthlyScores.value[path] = [
            ...(monthlyScores.value[path] ?? []),
            ...result.docs,
          ];
        } else {
          monthlyScores.value[path] = result.docs;
        }
      }

      // Store pagination state
      lastDocuments.value[path] = result.lastDoc;
      hasMoreData.value[path] = result.hasMore;

      // Clear fetching state
      if (knownUsers) {
        fetchingMonthlyScoresOnlyKnownUsers.value[path] = false;
      } else {
        fetchingMonthlyScores.value[path] = false;
      }
    }

    /* Exposed store */
    return {
      // State
      selectedMonth,
      selectedYear,
      monthlyScores,
      monthlyScoresOnlyKnownUsers,
      fetchingMonthlyScores,
      fetchingMonthlyScoresOnlyKnownUsers,
      onlyKnownUsers,
      lastDocuments,
      hasMoreData,

      // Computed
      currentMonthlyScores,
      isFetchingMonthlyScores,
      hasMore,

      // Actions
      setSelectedMonth,
      setSelectedYear,
      setOnlyKnownUsersWordleMonthlyScores,
      reset,
      fetchMonthlyScores: fetchMonthlyScoresAction,
    };
  },
);
